/**
 * Miroir FRONT de la matrice d'accès (backend/utils/permissions.py).
 *
 * La SOURCE DE VÉRITÉ reste le backend : /api/auth/me renvoie les permissions
 * déjà résolues (`{role, role_label, pages:[], data_tiers:{}, can_write}`). Le front
 * ne recopie donc PAS la logique de rôle — il lit `pages[]`. On garde ici juste la
 * règle « une page Finance exige aussi le tier finance » (défense en profondeur,
 * identique à can_open_page côté serveur) + le helper de lecture.
 */

// Doit rester synchronisé avec FINANCE_PAGES (permissions.py).
export const FINANCE_PAGES = new Set(['rentabilite', 'marge', 'devis', 'factures', 'banque']);

// Catalogue des pages pour l'éditeur d'accès (Administration). Groupé pour l'UI.
// Les clés = PAGE_KEYS (permissions.py). L'ordre suit le menu.
export const PAGE_CATALOG = [
  { group: 'Général', pages: [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'inbox', label: 'Inbox' },
    { key: 'suivi', label: 'Suivi direction' },
    { key: 'ia', label: 'Hub IA' },
  ] },
  { group: 'Finance', pages: [
    { key: 'rentabilite', label: 'Rentabilité' },
    { key: 'marge', label: 'Marge' },
    { key: 'devis', label: 'Devis' },
    { key: 'factures', label: 'Factures' },
    { key: 'banque', label: 'Banque' },
  ] },
  { group: 'Commercial', pages: [
    { key: 'crm', label: 'CRM / Scanner' },
    { key: 'appels-offre', label: "Appels d'offres" },
    { key: 'clients', label: 'Clients' },
  ] },
  // Réorg 17/07 : « Équipe » devient Exploitation (Collaborateurs/Conformité incluses),
  // la paperasse RH (Contrats/Paie/Heures/DPAE/Fichiers) a sa propre section — mêmes
  // groupes que le menu. Le groupe n'a AUCUN effet de droit, seule la clé compte.
  { group: 'Exploitation', pages: [
    { key: 'sites', label: 'Sites' },
    { key: 'planning', label: 'Planning' },
    { key: 'terrain', label: 'Terrain / Main courante' },
    { key: 'sondages', label: 'Sondages & dispos' },
    { key: 'employes', label: 'Collaborateurs' },
    { key: 'compliance', label: 'Conformité' },
  ] },
  { group: 'RH', pages: [
    { key: 'hr', label: 'Contrats' },
    { key: 'paie', label: 'Fiches de paie' },
    { key: 'heures', label: 'Heures' },
    { key: 'dpae', label: 'DPAE' },
    { key: 'documents', label: 'Fichiers' },
  ] },
  { group: 'Administration', pages: [
    { key: 'administration', label: 'Administration (comptes)' },
  ] },
];

// Tiers de données sensibles (toggles éditeur).
export const TIER_CATALOG = [
  { key: 'rh', label: 'Données RH', hint: 'Salaire · NIR · coût employeur sur les fiches' },
  { key: 'finance', label: 'Finance', hint: 'Montants & modules financiers' },
  { key: 'strategy', label: 'Stratégie', hint: 'ROADMAP confidentiels · gouvernance IA' },
];

// `settings` (réglages perso) n'est jamais gardé par page.
const ALWAYS_ALLOWED = new Set(['settings']);

/**
 * canOpenPage(permissions, page) — le compte peut-il ouvrir `page` ?
 * `permissions` = objet effectif de /auth/me (ou null/undefined si absent).
 * Renvoie false si `permissions` est absent/malformé (fail-closed) — l'appelant
 * (AuthContext) gère un repli sûr pour ne pas verrouiller un admin en cas de blip.
 */
export function canOpenPage(permissions, page) {
  if (ALWAYS_ALLOWED.has(page)) return true;
  if (!permissions || !Array.isArray(permissions.pages)) return false;
  if (!permissions.pages.includes(page)) return false;
  if (FINANCE_PAGES.has(page) && !(permissions.data_tiers && permissions.data_tiers.finance)) return false;
  return true;
}
